import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  fetchGuardianChildren,
  updateChildSchoolYear,
} from '../services/guardians';
import {
  Card,
  CardContent,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '../components';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  ChildCare,
  School,
  Person,
  CalendarToday,
  VpnKey,
  Lock,
  Edit,
  Check,
  Close,
} from '@mui/icons-material';

const getSchoolYearLabel = (schoolYear) => {
  switch (schoolYear) {
    case 0:
      return 'Reception';
    case 1:
      return 'Year 1';
    case 2:
      return 'Year 2';
    case 3:
      return 'Year 3';
    case 4:
      return 'Year 4';
    case 5:
      return 'Year 5';
    case 6:
      return 'Year 6';
    default:
      return `Year ${schoolYear}`;
  }
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

function ChildrenSection({ refreshTrigger }) {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingChild, setEditingChild] = useState(null);
  const [editSchoolYear, setEditSchoolYear] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);

  const loadChildren = async () => {
    setLoading(true);
    setError('');

    try {
      const { data, error: fetchError } = await fetchGuardianChildren();

      if (fetchError) {
        setError('Failed to load children. Please try again.');
        console.error('Error fetching children:', fetchError);
      } else {
        setChildren(data || []);
      }
    } catch (err) {
      setError('An unexpected error occurred while loading children.');
      console.error('Exception in loadChildren:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChildren();
  }, [refreshTrigger]);

  const handleEditStart = (child) => {
    setEditingChild(child.id);
    setEditSchoolYear(child.school_year?.toString() || '');
  };

  const handleEditCancel = () => {
    setEditingChild(null);
    setEditSchoolYear('');
  };

  const handleEditSave = async (child) => {
    if (editSchoolYear === '') {
      setError('School year is required');
      return;
    }

    setUpdateLoading(true);
    setError('');

    try {
      const { error: updateError } = await updateChildSchoolYear(
        child.id,
        Number(editSchoolYear)
      );

      if (updateError) {
        setError('Failed to update school year. Please try again.');
        console.error('Error updating school year:', updateError);
      } else {
        // Update the local state
        setChildren((prev) =>
          prev.map((c) =>
            c.id === child.id
              ? { ...c, school_year: Number(editSchoolYear) }
              : c
          )
        );
        setEditingChild(null);
        setEditSchoolYear('');
      }
    } catch (err) {
      setError('An unexpected error occurred while updating school year.');
      console.error('Exception in handleEditSave:', err);
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <ChildCare color="primary" />
          Your Children
        </Typography>
        <Card elevation={1}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress color="primary" />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading children...
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <ChildCare color="primary" />
          Your Children
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (children.length === 0) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <ChildCare color="primary" />
          Your Children
        </Typography>
        <Card elevation={1}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <ChildCare sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="text.secondary">
              No Children Yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a child account to get started with your family&apos;s
              learning journey.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h5"
        component="h2"
        sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <ChildCare color="primary" />
        Your Children ({children.length})
      </Typography>

      <Stack spacing={2}>
        {children.map((child, index) => (
          <Card
            key={child.id || index}
            elevation={1}
            sx={{ overflow: 'visible' }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                {/* Top Section: Avatar, Basic Info, School Year */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  {/* Avatar and Basic Info */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flex: { sm: 1 },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: 56,
                        height: 56,
                        fontSize: '1.5rem',
                      }}
                    >
                      {child.name ? (
                        child.name.charAt(0).toUpperCase()
                      ) : (
                        <Person />
                      )}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          wordBreak: 'break-word',
                        }}
                      >
                        {child.name || 'Unnamed Child'}
                      </Typography>

                      {child.created_at && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <CalendarToday fontSize="inherit" />
                          Added {formatDate(child.created_at)}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* School Year */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: { xs: 'flex-start', sm: 'flex-end' },
                      gap: 1,
                    }}
                  >
                    {editingChild === child.id ? (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel id={`school-year-edit-${child.id}`}>
                            School Year
                          </InputLabel>
                          <Select
                            labelId={`school-year-edit-${child.id}`}
                            value={editSchoolYear}
                            label="School Year"
                            onChange={(e) => setEditSchoolYear(e.target.value)}
                            disabled={updateLoading}
                          >
                            <MenuItem value={0}>Reception</MenuItem>
                            <MenuItem value={1}>Year 1</MenuItem>
                            <MenuItem value={2}>Year 2</MenuItem>
                            <MenuItem value={3}>Year 3</MenuItem>
                            <MenuItem value={4}>Year 4</MenuItem>
                            <MenuItem value={5}>Year 5</MenuItem>
                            <MenuItem value={6}>Year 6</MenuItem>
                          </Select>
                        </FormControl>
                        <IconButton
                          size="small"
                          onClick={() => handleEditSave(child)}
                          disabled={updateLoading}
                          color="primary"
                        >
                          <Check />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleEditCancel}
                          disabled={updateLoading}
                          color="default"
                        >
                          <Close />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {typeof child.school_year === 'number' && (
                          <Chip
                            icon={<School />}
                            label={getSchoolYearLabel(child.school_year)}
                            color="secondary"
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleEditStart(child)}
                          color="primary"
                          title="Edit school year"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Login Credentials Section */}
                {(child.username || child.password || child.guardian_code) && (
                  <Box
                    sx={{
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'grey.200',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        color: 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <VpnKey fontSize="small" />
                      Login Credentials
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                        gap: 2,
                      }}
                    >
                      {child.username && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.5 }}
                          >
                            Username
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              bgcolor: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Person fontSize="small" />
                            {child.username}
                          </Typography>
                        </Box>
                      )}

                      {child.password && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.5 }}
                          >
                            Password
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              bgcolor: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Lock fontSize="small" />
                            {child.password}
                          </Typography>
                        </Box>
                      )}

                      {child.guardian_code && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.5 }}
                          >
                            Guardian Code
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              bgcolor: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <VpnKey fontSize="small" />
                            {child.guardian_code}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

ChildrenSection.propTypes = {
  refreshTrigger: PropTypes.number,
};

ChildrenSection.defaultProps = {
  refreshTrigger: 0,
};

export default ChildrenSection;
